import { useRef } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { LayoutGrid, Flower2, MessageCircle, User } from 'lucide-react';

import { cn } from '@llura/ui';

import { useCoachStore } from '../stores/coach.store';
import { useTodayArchetype } from '../stores/progress.store';
import { useSession } from '../lib/auth-client';
import { AUTH_REQUIRED } from '../lib/config';
import { useStrings } from '../hooks/useStrings';
import { useScrollToTop } from '../hooks/use-scroll-to-top';

export function AppShell() {
  const isConfigured = useCoachStore((s) => s.isConfigured);
  const session = useSession();
  const location = useLocation();
  const { strings } = useStrings();
  const mainRef = useRef<HTMLElement>(null);
  const todayArchetype = useTodayArchetype();

  useScrollToTop(location.pathname, mainRef);

  const ritualTarget = `/workout/${todayArchetype ?? 'priestess'}`;
  const navItems = [
    { to: '/dashboard', label: strings.nav.space, icon: LayoutGrid, match: '/dashboard' },
    { to: ritualTarget, label: strings.nav.ritual, icon: Flower2, match: '/workout' },
    { to: '/onboarding/chat', label: strings.nav.coach, icon: MessageCircle, match: '/onboarding/chat' },
    { to: '/profile', label: strings.nav.profile, icon: User, match: '/profile' },
  ] as const;

  if (!isConfigured) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // When auth is required, the app shell is session-gated. Wait for the
  // session check to resolve to avoid bouncing signed-in users on reload.
  if (AUTH_REQUIRED && !session.isPending && !session.data) {
    return <Navigate to="/onboarding/signup" replace />;
  }

  return (
    <div className="flex min-h-[100svh] flex-col bg-background text-foreground">
      <main ref={mainRef} className="flex-1 overflow-y-auto pb-24">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/90 pb-[max(env(safe-area-inset-bottom),0.5rem)] backdrop-blur">
        <ul className="mx-auto flex max-w-md items-stretch justify-around px-2">
          {navItems.map(({ to, label, icon: Icon, match }) => {
            const isActive =
              location.pathname === to || location.pathname.startsWith(match);
            return (
              <li key={to} className="flex-1">
                <NavLink
                  to={to}
                  className={cn(
                    'flex flex-col items-center gap-1 py-3 text-[10px] uppercase tracking-[0.15em] transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  <Icon className={cn('h-5 w-5', isActive && 'drop-shadow-[0_0_8px_hsl(var(--primary)/0.8)]')} />
                  {label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
